import { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import { Controller } from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage,
  faXmark,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import type { ImageMemo } from 'models/Memo';
import { MediaImage } from 'elements/Media';
import TitleField from '../fields/TitleField';
import { useTranslations } from 'next-intl';
import { useMemoFormContext } from '../context/FormContext';
import { useMediaUpload } from '../hooks/useMediaUpload';

interface Props {
  memo?: ImageMemo | null;
}

const ImageForm: FC<Props> = ({ memo }) => {
  const { control, isTitleVisible } = useMemoFormContext();
  const t = useTranslations();
  const { selectedBox } = useActiveSpace();
  const boxId = memo?.boxId ?? selectedBox?.id;

  const { items, addFiles, removeItem } = useMediaUpload({
    name: 'imageIds',
    boxId,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const pickFiles = () => inputRef.current?.click();

  const onSelected = (files: FileList | null) => {
    addFiles(
      Array.from(files ?? []).filter((f) => f.type.startsWith('image/')),
    );
  };

  return (
    <div className="space-y-3">
      {isTitleVisible && <TitleField />}
      <Controller
        control={control}
        name="content"
        render={({ field: { onChange, value, onBlur } }) => (
          <TextareaAutosize
            minRows={1}
            maxRows={5}
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            className={styles.caption}
            placeholder={t('memoComposer.descriptionPlaceholder')}
          />
        )}
      />

      {items.length === 0 ? (
        <button
          type="button"
          onClick={pickFiles}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            onSelected(e.dataTransfer.files);
          }}
          className={classNames(styles.dropzone, isDragging && styles.dragging)}
        >
          <FontAwesomeIcon icon={faImage} className={styles.dropIcon} />
          <span className={styles.dropTitle}>
            {t('memoComposer.image.add')}
          </span>
          <span className={styles.dropHint}>
            {t('memoComposer.image.hint')}
          </span>
        </button>
      ) : (
        <div className={styles.grid}>
          {items.map((item) => (
            <div key={item.key} className={styles.tile}>
              {item.isExisting && item.mediaId ? (
                <MediaImage mediaId={item.mediaId} className={styles.img} />
              ) : (
                <img src={item.previewUrl} alt="" className={styles.img} />
              )}

              {item.status === 'uploading' && (
                <div className={styles.overlay}>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                </div>
              )}
              {item.status === 'error' && (
                <div className={classNames(styles.overlay, styles.error)}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </div>
              )}

              <button
                type="button"
                onClick={() => removeItem(item.key)}
                aria-label={t('memoComposer.image.remove')}
                className={styles.remove}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ))}

          <button type="button" onClick={pickFiles} className={styles.addMore}>
            <FontAwesomeIcon icon={faImage} />
            <span className="text-xs">{t('memoComposer.addMore')}</span>
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          onSelected(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default ImageForm;

const styles = {
  dropzone: classNames(
    'flex w-full flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed',
    'border-stroke bg-surface-field py-10 text-ink-secondary transition-colors hover:bg-surface-field focus:outline-none',
  ),
  dragging: 'border-accent-ink bg-accent-tint text-accent-ink',
  dropIcon: 'text-2xl',
  dropTitle: 'text-sm font-semibold text-ink-primary',
  dropHint: 'text-xs text-ink-tertiary',
  grid: 'grid grid-cols-3 gap-2',
  tile: 'relative aspect-square overflow-hidden rounded-xl bg-surface-field',
  img: 'h-full w-full object-cover',
  overlay:
    'absolute inset-0 flex items-center justify-center bg-black/40 text-white',
  error: 'bg-mf-red/60',
  remove:
    'absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs text-white hover:bg-black/70',
  addMore: classNames(
    'flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed',
    'border-stroke text-ink-tertiary hover:bg-surface-field focus:outline-none',
  ),
  caption:
    'block w-full bg-transparent resize-none border-0 px-0 py-0 text-base text-ink-primary placeholder-ink-tertiary focus:ring-0',
};
